/* Finding a time several people can do.
 *
 * The Doodle flow, with one difference that is the whole point: the options
 * are not guessed. For anybody who has connected and shared even that they are
 * busy, Amon already knows when they are not free, so it proposes times that
 * can actually happen and the poll settles the rest.
 *
 * What the screen must never do is imply more than it knows. A suggestion says
 * "2 of 3 free · 1 unknown" — never "everybody can make this" — because the
 * person who shares nothing has told us nothing, and pretending otherwise is
 * how somebody ends up inviting a colleague to a meeting during their child's
 * hospital appointment.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, del, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import { useSession, localToday } from '../../session';
import { friendlyDate, human } from '../../ui/time';
import Section from '../../ui/Section';
import Icon from '../../ui/Icon';

interface Slot {
  id?: string; date: string; startMin: number; start: string; end: string;
  suggested?: boolean; free?: number; freeOf?: number; unknown?: number;
  yes?: number; maybe?: number; no?: number;
}

interface Meeting {
  id: string; title: string; minutes: number; notes: string | null;
  decidedSlot: string | null;
  slots: Slot[];
  people: { id: string; name: string; answered: boolean; token?: string }[];
  answered: number; invited: number;
}

interface Person { id: string; name: string }

export default function Meetings({ connections }: { connections: Person[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user, today } = useSession();
  const date = today ?? localToday(user?.tz);

  const [making, setMaking] = useState(false);
  const [title, setTitle] = useState('');
  const [minutes, setMinutes] = useState('30');
  const [withIds, setWithIds] = useState<string[]>([]);
  const [outsiders, setOutsiders] = useState('');
  const [chosen, setChosen] = useState<Slot[]>([]);
  const [error, setError] = useState<string | null>(null);

  const meetings = useQuery({
    queryKey: ['meetings'],
    queryFn: async () => {
      const r = await get<{ meetings: Meeting[] }>('/api/meetings');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your meetings.'));
      return r.meetings;
    }
  });

  const suggested = useQuery({
    queryKey: ['meeting-suggest', minutes, withIds.join(',')],
    enabled: making,
    queryFn: async () => {
      const r = await get<{ slots: Slot[] }>(
        `/api/meetings/suggest?from=${date}&minutes=${Number(minutes) || 30}` +
        `&with=${withIds.join(',')}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not work out any times.'));
      return r.slots;
    }
  });

  const create = useMutation({
    mutationFn: async () => {
      const invite = [
        ...withIds.map(id => ({ userId: id })),
        ...outsiders.split(',').map(n => n.trim()).filter(Boolean).map(name => ({ name }))
      ];
      const r = await post<{ meeting: Meeting }>('/api/meetings', {
        title: title.trim(),
        minutes: Math.max(10, Math.min(480, Number(minutes) || 30)),
        slots: chosen.map(s => ({ date: s.date, startMin: s.startMin, suggested: !!s.suggested })),
        invite
      });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not set that up.'));
      return r.meeting;
    },
    onSuccess: m => {
      void qc.invalidateQueries({ queryKey: ['meetings'] });
      setMaking(false);
      setTitle(''); setChosen([]); setWithIds([]); setOutsiders('');
      toast.say(`“${m.title}” is out to ${m.invited} people. Send them their links.`, 'good');
    },
    onError: (e: Error) => setError(e.message)
  });

  const decide = useMutation({
    mutationFn: async ({ m, slotId }: { m: Meeting; slotId: string }) => {
      const r = await post(`/api/meetings/${m.id}/decide`, { slotId });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not book that.'));
      return m;
    },
    onSuccess: m => {
      void qc.invalidateQueries();
      toast.say(`“${m.title}” is on your day. Everybody else has been told the time.`, 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const drop = useMutation({
    mutationFn: async (m: Meeting) => {
      const r = await del(`/api/meetings/${m.id}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not remove that.'));
      return m;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['meetings'] }); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const toggleSlot = (s: Slot) =>
    setChosen(list => list.some(x => x.date === s.date && x.startMin === s.startMin)
      ? list.filter(x => !(x.date === s.date && x.startMin === s.startMin))
      : [...list, s]);

  const list = meetings.data ?? [];

  return (
    <Section id="meet-title" icon="calendar" title="Finding a time together"
             meta={list.length ? `${list.filter(m => !m.decidedSlot).length} being arranged` : 'a poll where the options can actually happen'}
             info={<>Like a poll, except the options are not guesses: for anybody who has shared
               even that they are busy, Amon proposes times that can actually happen. The
               vote settles the rest. People without an account answer from a link.</>}
             action={!making && (
               <button className="btn btn-primary btn-sm" onClick={() => setMaking(true)}>
                 <Icon name="plus" size={16} />Find a time
               </button>
             )}>

      {making && (
        <form className="when-form" onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!title.trim()) { setError('What is the meeting?'); return; }
          if (!chosen.length) { setError('Pick at least one time to offer.'); return; }
          setError(null);
          create.mutate();
        }}>
          <div className="field field-wide">
            <label htmlFor="meet-title-input">What is it?</label>
            <input id="meet-title-input" value={title} maxLength={120} autoFocus
                   placeholder="Design review"
                   onChange={e => setTitle(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="meet-mins">How long</label>
            <select id="meet-mins" value={minutes} onChange={e => setMinutes(e.target.value)}>
              {[15, 30, 45, 60, 90].map(m => <option key={m} value={m}>{human(m)}</option>)}
            </select>
          </div>

          {connections.length > 0 && (
            <fieldset className="field-wide alarm-picker">
              <legend>Who, of the people you are connected to</legend>
              {connections.map(p => (
                <label key={p.id} className={`chip chip-pick${withIds.includes(p.id) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={withIds.includes(p.id)}
                         onChange={() => setWithIds(l =>
                           l.includes(p.id) ? l.filter(x => x !== p.id) : [...l, p.id])} />
                  {p.name}
                </label>
              ))}
            </fieldset>
          )}

          <div className="field field-wide">
            <label htmlFor="meet-outsiders">
              And anybody else <span className="muted">(names, separated by commas)</span>
            </label>
            <input id="meet-outsiders" value={outsiders} maxLength={300}
                   placeholder="Sara, the printer people"
                   onChange={e => setOutsiders(e.target.value)} />
            <span className="muted small">
              They get a link and answer without an account.
            </span>
          </div>

          <div className="field-wide">
            <h3>Times to offer</h3>
            {suggested.isPending && <p className="muted small">Working out when everybody is free…</p>}
            {suggested.data?.length === 0 && (
              <p className="muted small">
                Nothing free in your own working hours over the next ten days. Widen your
                hours, or clear something first.
              </p>
            )}
            <ul className="slot-list">
              {(suggested.data ?? []).map(s => {
                const picked = chosen.some(x => x.date === s.date && x.startMin === s.startMin);
                return (
                  <li key={`${s.date}-${s.startMin}`}>
                    <label className={`chip chip-pick${picked ? ' is-on' : ''}`}>
                      <input type="checkbox" checked={picked} onChange={() => toggleSlot(s)} />
                      {friendlyDate(s.date)} · {s.start}
                    </label>
                    <span className="muted small">
                      {/* Never "everybody can make this": the people who share
                          nothing have told us nothing. */}
                      {s.freeOf ? `${s.free} of ${s.freeOf} free` : 'nobody’s calendar to check'}
                      {s.unknown ? ` · ${s.unknown} unknown` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {error && <p className="auth-err field-wide" role="alert">{error}</p>}

          <div className="inline field-wide">
            <button className="btn btn-primary btn-sm" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Setting up…' : `Ask about ${chosen.length || 'these'} times`}
            </button>
            <button className="btn btn-ghost btn-sm" type="button"
                    onClick={() => setMaking(false)}>Cancel</button>
          </div>
        </form>
      )}

      {list.length === 0 && !making && (
        <p className="muted small">Nothing being arranged.</p>
      )}

      {list.map(m => (
        <article key={m.id} className="meeting">
          <div className="panel-head">
            <h3>{m.title}</h3>
            <span className="muted small">
              {m.answered} of {m.invited} answered
              {m.decidedSlot ? ' · booked' : ''}
            </span>
          </div>

          <ul className="slot-list">
            {m.slots.map(s => (
              <li key={s.id} className={m.decidedSlot === s.id ? 'is-chosen' : ''}>
                <div>
                  <strong>{friendlyDate(s.date)} · {s.start}–{s.end}</strong>
                  <span className="muted small">
                    {' '}· {s.yes ?? 0} yes
                    {s.maybe ? `, ${s.maybe} maybe` : ''}
                    {s.no ? `, ${s.no} no` : ''}
                    {s.suggested ? ' · suggested' : ''}
                  </span>
                </div>
                {!m.decidedSlot && (
                  <button className="btn btn-ghost btn-sm"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ m, slotId: s.id! })}>
                    <Icon name="check" size={15} />Book this
                  </button>
                )}
              </li>
            ))}
          </ul>

          <div className="inline wrap">
            {m.people.filter(p => p.token).map(p => (
              <button key={p.id} className="btn btn-ghost btn-sm"
                      onClick={() => {
                        const link = `${location.origin}/answer/${p.token}`;
                        void navigator.clipboard?.writeText(link)
                          .then(() => toast.say(`Link for ${p.name} copied.`))
                          .catch(() => toast.say(link));
                      }}>
                <Icon name={p.answered ? 'check' : 'copy'} size={14} />Copy {p.name}’s link
              </button>
            ))}
            <button className="btn btn-ghost btn-sm btn-bad" onClick={() => drop.mutate(m)}><Icon name="trash" size={15} />Remove</button>
          </div>
        </article>
      ))}
    </Section>
  );
}
